/**
 * Stage 73.20cv — ESS Statutory Forms.
 * ------------------------------------------------------------------
 * Single ESS page with 4 tabs, one per statutory form:
 *   1. Joining Form            (personal + address + edu + prev-emp + statutory + bank)
 *   2. Form F — PF Nomination  (nominee list, share %)
 *   3. Gratuity Nomination     (nominee list, share %)
 *   4. Health Insurance        (policy selection + insured members)
 *
 * Each form supports Save Draft + Submit for HR. Submitted forms are
 * locked; HR approves or rejects. Approved forms can be downloaded as
 * PDF (Rupee-symbol-capable font).
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import {
  Loader2, Save, Send, Download, FileText, Trash2, Plus,
  Users as UsersIcon, KeyRound, Building2, Stethoscope, Briefcase, Camera, X, FileCheck, CheckSquare, RefreshCw, Upload,
  AlertCircle, GraduationCap, Edit3, Calendar, Shield,
} from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { AuthenticatedImage } from '../components/AuthenticatedImage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FORM_META = [
  { key: 'joining',           label: 'Employee Details',              icon: Briefcase,   desc: 'Personal, address, education, previous employer, statutory details, and bank information.' },
  { key: 'form_f',            label: 'PF Nomination (EPF Form 2)',    icon: KeyRound,    desc: 'Employee Provident Fund (EPF) nomination and family declaration under EPF Form 2.', tooltip: 'Employee Provident Fund Nomination (EPF Form 2)' },
  { key: 'gratuity',          label: 'Gratuity Nomination (Form F)',   icon: UsersIcon,   desc: 'Nomination under the Payment of Gratuity Act, 1972 (Form F).', tooltip: 'Payment of Gratuity Act, 1972 – Nomination (Form F)' },
  { key: 'esi_application',   label: 'ESI Application',               icon: Shield,      desc: 'ESI application for Gross Salary ≤ ₹21,000.', tooltip: 'Employee State Insurance (ESI) Application Form' },
  { key: 'health_insurance',  label: 'Health Insurance',              icon: Stethoscope, desc: 'Health Insurance declaration for Gross Salary > ₹21,000.' },
  { key: 'acknowledgement',   label: 'Acknowledgement',             icon: FileCheck,   desc: 'Employee Handbook / HR Policy Manual Acknowledgement.', tooltip: 'Employee Handbook / HR Policy Manual Acknowledgement' },
];

// Field schemas per form. Each entry: { key, label, type, options? }
const FIELD_SCHEMAS = {
  joining: [
    { section: 'Personal', fields: [
      { key: 'full_name',              label: 'Full Name', required: true },
      { key: 'father_or_husband_name', label: "Father's / Husband's Name" },
      { key: 'date_of_birth',          label: 'Date of Birth', type: 'date' },
      { key: 'gender',                 label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'] },
      { key: 'marital_status',         label: 'Marital Status', type: 'select', options: ['Single', 'Married', 'Divorced', 'Widowed'] },
      { key: 'blood_group',            label: 'Blood Group' },
      { key: 'nationality',            label: 'Nationality' },
      { key: 'personal_email',         label: 'Personal Email' },
      { key: 'mobile',                 label: 'Mobile' },
    ] },
    { section: 'Address', fields: [
      { key: 'permanent_address', label: 'Permanent Address', type: 'textarea' },
      { key: 'current_address',   label: 'Current Address',   type: 'textarea' },
      { key: 'city',              label: 'City' },
      { key: 'state',             label: 'State' },
      { key: 'pincode',           label: 'Pincode' },
    ] },
    { section: 'Emergency Contact', fields: [
      { key: 'emergency_contact_name',         label: 'Name' },
      { key: 'emergency_contact_relationship', label: 'Relationship' },
      { key: 'emergency_contact_number',       label: 'Phone' },
    ] },
    { section: 'Education (Highest)', fields: [
      { key: 'highest_qualification', label: 'Qualification' },
      { key: 'institution',           label: 'Institution' },
      { key: 'year_of_passing',       label: 'Year of Passing' },
      { key: 'specialisation',        label: 'Specialisation' },
    ] },
    { section: 'Previous Employer', fields: [
      { key: 'prev_employer_name', label: 'Employer Name' },
      { key: 'prev_designation',   label: 'Designation' },
      { key: 'prev_from',          label: 'From (Date)', type: 'date' },
      { key: 'prev_to',            label: 'To (Date)',   type: 'date' },
      { key: 'prev_reason',        label: 'Reason for Leaving' },
    ] },
    { section: 'Bank Details', fields: [
      { key: 'bank_name',       label: 'Bank Name' },
      { key: 'account_number',  label: 'Account Number' },
      { key: 'ifsc_code',       label: 'IFSC' },
      { key: 'bank_branch',     label: 'Branch' },
    ] },
    { section: 'Statutory IDs', fields: [
      { key: 'pan_number',          label: 'PAN' },
      { key: 'aadhaar_number',      label: 'Aadhaar' },
      { key: 'uan_number',          label: 'UAN (if any)' },
      { key: 'previous_pf_number',  label: 'Previous PF Number' },
    ] },
  ],
  form_f: [
    { section: 'Declaration', fields: [
      { key: 'full_name',              label: 'Full Name', required: true },
      { key: 'father_or_husband_name', label: "Father's / Husband's Name" },
      { key: 'date_of_birth',          label: 'Date of Birth', type: 'date' },
      { key: 'gender',                 label: 'Sex', type: 'select', options: ['Male', 'Female'] },
      { key: 'marital_status',         label: 'Marital Status', type: 'select', options: ['Single', 'Married'] },
      { key: 'permanent_address',      label: 'Permanent Address', type: 'textarea' },
    ] },
    { section: 'Nomination', fields: [
      { key: 'epf_account_number', label: 'EPF Account Number' },
      { key: 'eps_nominee_same',   label: 'EPS (Pension) Nominee same as PF?', type: 'select', options: ['Yes', 'No'] },
    ] },
    { section: 'Nominees', repeater: 'nominees', fields: [
      { key: 'name',           label: 'Nominee Name', required: true },
      { key: 'relationship',   label: 'Relationship' },
      { key: 'date_of_birth',  label: 'Date of Birth', type: 'date' },
      { key: 'percentage',     label: 'Share %', type: 'number' },
      { key: 'address',        label: 'Address', type: 'textarea' },
    ] },
  ],
  gratuity: [
    { section: 'Declaration', fields: [
      { key: 'full_name',         label: 'Full Name', required: true },
      { key: 'date_of_birth',     label: 'Date of Birth', type: 'date' },
      { key: 'date_of_joining',   label: 'Date of Joining', type: 'date' },
      { key: 'marital_status',    label: 'Marital Status', type: 'select', options: ['Single', 'Married'] },
      { key: 'permanent_address', label: 'Permanent Address', type: 'textarea' },
    ] },
    { section: 'Family Members / Nominees', repeater: 'nominees', fields: [
      { key: 'name',           label: 'Name', required: true },
      { key: 'relationship',   label: 'Relationship' },
      { key: 'date_of_birth',  label: 'Date of Birth', type: 'date' },
      { key: 'percentage',     label: 'Share %', type: 'number' },
      { key: 'address',        label: 'Address', type: 'textarea' },
    ] },
  ],
  health_insurance: [
    { section: 'Policy Selection', fields: [
      { key: 'policy_type',    label: 'Policy Type', type: 'select', options: ['Self', 'Self + Spouse', 'Self + Family', 'Self + Family + Parents'] },
      { key: 'sum_insured',    label: 'Sum Insured Elected', type: 'select', options: ['3,00,000', '5,00,000', '10,00,000', '15,00,000'] },
      { key: 'top_up_opted',   label: 'Top-Up Opted?', type: 'select', options: ['Yes', 'No'] },
      { key: 'top_up_amount',  label: 'Top-Up Amount' },
      { key: 'parental_cover', label: 'Parental Cover?', type: 'select', options: ['Yes', 'No'] },
    ] },
    { section: 'Insured — Self', fields: [
      { key: 'full_name',          label: 'Full Name' },
      { key: 'date_of_birth',      label: 'Date of Birth', type: 'date' },
      { key: 'gender',             label: 'Gender', type: 'select', options: ['Male', 'Female'] },
      { key: 'self_pre_existing',  label: 'Pre-existing Conditions', type: 'textarea' },
    ] },
    { section: 'Dependents', repeater: 'nominees', fields: [
      { key: 'name',           label: 'Name', required: true },
      { key: 'relationship',   label: 'Relationship', type: 'select', options: ['Spouse', 'Child', 'Parent', 'Parent-in-law'] },
      { key: 'date_of_birth',  label: 'Date of Birth', type: 'date' },
      { key: 'gender',         label: 'Gender', type: 'select', options: ['Male', 'Female'] },
      { key: 'address',        label: 'Notes', type: 'textarea' },
    ] },
  ],
};

const StatusBadge = ({ status }) => {
  const map = {
    draft:     { cls: 'bg-slate-100 text-slate-700', label: 'Draft' },
    submitted: { cls: 'bg-amber-100 text-amber-800', label: 'Submitted' },
    approved:  { cls: 'bg-emerald-100 text-emerald-700', label: 'Approved' },
    rejected:  { cls: 'bg-red-100 text-red-700', label: 'Rejected' },
  };
  const m = map[status] || map.draft;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${m.cls}`}>{m.label}</span>;
};

const FieldInput = ({ f, value, onChange, disabled, error }) => {
  const errClass = error ? 'border-red-400 bg-red-50 focus:ring-red-300' : 'border-slate-300';
  if (f.type === 'select') {
    return (
      <>
        <select value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled}
          data-testid={`form-field-${f.key}`}
          className={`w-full border rounded-md px-3 py-2 text-sm bg-white disabled:bg-slate-50 ${errClass}`}>
          <option value="">— select —</option>
          {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {error && <p className="text-red-500 text-[11px] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3 shrink-0" />{error}</p>}
      </>
    );
  }
  if (f.type === 'textarea') {
    return (
      <>
        <textarea rows={3} value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled}
          data-testid={`form-field-${f.key}`}
          className={`w-full border rounded-md px-3 py-2 text-sm disabled:bg-slate-50 ${errClass}`} />
        {error && <p className="text-red-500 text-[11px] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3 shrink-0" />{error}</p>}
      </>
    );
  }
  return (
    <>
      <input type={f.type || 'text'} value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled}
        data-testid={`form-field-${f.key}`}
        className={`w-full border rounded-md px-3 py-2 text-sm disabled:bg-slate-50 ${errClass}`} />
      {error && <p className="text-red-500 text-[11px] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3 shrink-0" />{error}</p>}
    </>
  );
};

const SignatureCanvas = ({ onSave, readOnly, existingSignature }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(!!existingSignature);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (existingSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasDrawn(true);
      };
      img.src = existingSignature;
    }
  }, [existingSignature]);

  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    
    let clientX = e.clientX;
    let clientY = e.clientY;

    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    }

    const scaleX = canvas.width / (rect.width || 1);
    const scaleY = canvas.height / (rect.height || 1);

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    return { x, y };
  };

  const startDrawing = (e) => {
    if (readOnly) return;
    if (e.cancelable) e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = '#0F172A';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || readOnly) return;
    if (e.cancelable) e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    if (e && e.cancelable) e.preventDefault();
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas && onSave) {
      onSave(canvas.toDataURL('image/png'));
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    if (onSave) onSave('');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Signature image size must be under 2 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      const dataUrl = evt.target.result;
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          setHasDrawn(true);
          if (onSave) onSave(dataUrl);
        };
        img.src = dataUrl;
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3">
      <div className="relative border-2 border-slate-300 border-dashed rounded-xl bg-slate-50 overflow-hidden shadow-inner">
        <canvas
          ref={canvasRef}
          width={500}
          height={130}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          data-testid="signature-canvas"
          className={`w-full h-32 touch-none ${readOnly ? 'cursor-not-allowed opacity-90' : 'cursor-crosshair'}`}
        />
        {!hasDrawn && !readOnly && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 pointer-events-none text-xs">
            Draw signature here using mouse or touch screen
          </div>
        )}
      </div>

      {!readOnly && (
        <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearCanvas}
              data-testid="btn-clear-signature"
              className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs font-semibold flex items-center gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear Signature
            </button>
            <label className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs font-semibold flex items-center gap-1 cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> Upload Image
              <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" data-testid="upload-signature-file" />
            </label>
          </div>
          <span className="text-slate-400 text-[11px]">Signature is required for legal acknowledgment submission.</span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Experience Calculation Helper
// ---------------------------------------------------------------------------
export function calculateExperience(fromDate, toDate) {
  if (!fromDate || !toDate) return '';
  const start = new Date(fromDate);
  const end = new Date(toDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return '';

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();

  if (days < 0) {
    months--;
  }
  if (months < 0) {
    years--;
    months += 12;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
  if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
  if (parts.length === 0) return 'Less than 1 Month';
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// Multi-Record Education Component
// ---------------------------------------------------------------------------
const EducationSection = ({ data, setData, readOnly }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [form, setForm] = useState({
    qualification: '', degree: '', institution: '', university: '', specialisation: '', year_of_passing: '', percentage: '', certificate: '',
  });

  const list = Array.isArray(data.education) ? data.education : [];

  const openAdd = () => {
    setForm({
      qualification: '', degree: '', institution: '', university: '', specialisation: '', year_of_passing: '', percentage: '', certificate: '',
    });
    setEditingIndex(null);
    setShowModal(true);
  };

  const openEdit = (idx) => {
    setForm({ ...list[idx] });
    setEditingIndex(idx);
    setShowModal(true);
  };

  const removeItem = (idx) => {
    if (!window.confirm('Delete this education record?')) return;
    const nextList = list.filter((_, i) => i !== idx);
    const lastItem = nextList[nextList.length - 1] || {};
    setData(prev => ({
      ...prev,
      education: nextList,
      highest_qualification: lastItem.qualification || lastItem.degree || '',
      institution: lastItem.institution || '',
      year_of_passing: lastItem.year_of_passing || '',
      specialisation: lastItem.specialisation || '',
    }));
  };

  const saveModal = () => {
    if (!form.qualification.trim() && !form.degree.trim()) {
      toast.error('Please enter Qualification or Degree.');
      return;
    }
    if (!form.institution.trim()) {
      toast.error('Please enter Institution / College.');
      return;
    }
    if (!form.year_of_passing) {
      toast.error('Please enter Year of Passing.');
      return;
    }
    const currentYear = new Date().getFullYear();
    const yr = parseInt(form.year_of_passing, 10);
    if (isNaN(yr) || yr < 1950 || yr > currentYear) {
      toast.error(`Year of Passing must be between 1950 and ${currentYear}.`);
      return;
    }

    const nextList = [...list];
    if (editingIndex !== null) {
      nextList[editingIndex] = form;
    } else {
      nextList.push(form);
    }

    const latest = nextList[nextList.length - 1] || {};
    setData(prev => ({
      ...prev,
      education: nextList,
      highest_qualification: latest.qualification || latest.degree || '',
      institution: latest.institution || '',
      year_of_passing: latest.year_of_passing || '',
      specialisation: latest.specialisation || '',
    }));
    setShowModal(false);
  };

  return (
    <div className="space-y-4" data-testid="education-multi-section">
      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <GraduationCap className="w-4 h-4 text-[#0052FF]" /> Education Details ({list.length})
          </h3>
          <p className="text-[11px] text-slate-500">Add multiple education records (10th, 12th, Degree, PG, Ph.D, etc.)</p>
        </div>
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            onClick={openAdd}
            data-testid="btn-add-education"
            className="bg-[#0052FF] hover:bg-[#0043D1] text-white text-xs flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Add Education
          </Button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-slate-200 rounded-lg bg-slate-50">
          <GraduationCap className="w-8 h-8 mx-auto text-slate-300 mb-1" />
          <p className="text-xs text-slate-500 font-medium">No education records added yet.</p>
          {!readOnly && <p className="text-[11px] text-slate-400 mt-0.5">Click "+ Add Education" above to add your qualifications.</p>}
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 text-slate-700 font-semibold uppercase border-b border-slate-200">
              <tr>
                <th className="p-2.5">Qualification</th>
                <th className="p-2.5">Degree</th>
                <th className="p-2.5">Institution</th>
                <th className="p-2.5">University / Board</th>
                <th className="p-2.5">Specialisation</th>
                <th className="p-2.5">Year</th>
                <th className="p-2.5">% / CGPA</th>
                {!readOnly && <th className="p-2.5 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50/80">
                  <td className="p-2.5 font-semibold text-slate-900">{item.qualification || '—'}</td>
                  <td className="p-2.5 text-slate-800">{item.degree || '—'}</td>
                  <td className="p-2.5 text-slate-700">{item.institution || '—'}</td>
                  <td className="p-2.5 text-slate-600">{item.university || '—'}</td>
                  <td className="p-2.5 text-slate-600">{item.specialisation || '—'}</td>
                  <td className="p-2.5 font-mono text-slate-800">{item.year_of_passing || '—'}</td>
                  <td className="p-2.5 font-mono font-semibold text-emerald-600">{item.percentage || '—'}</td>
                  {!readOnly && (
                    <td className="p-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(idx)}
                          data-testid={`edit-education-${idx}`}
                          className="p-1 text-slate-500 hover:text-[#0052FF] hover:bg-blue-50 rounded"
                          title="Edit Record"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          data-testid={`delete-education-${idx}`}
                          className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete Record"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Dialog for Add / Edit Education */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="modal-education-form">
          <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden shadow-2xl space-y-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50/80">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-[#0052FF]" />
                {editingIndex !== null ? 'Edit Education Record' : 'Add Education Record'}
              </h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Qualification Level <span className="text-red-500">*</span></label>
                <select
                  value={form.qualification}
                  onChange={e => setForm({ ...form, qualification: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs focus:ring-[#0052FF]"
                  data-testid="input-edu-qualification"
                >
                  <option value="">— Select —</option>
                  <option value="10th SSLC">10th Standard / SSLC</option>
                  <option value="12th HSC">12th Standard / HSC</option>
                  <option value="Diploma">Diploma</option>
                  <option value="Bachelor Degree (UG)">Bachelor Degree (UG)</option>
                  <option value="Master Degree (PG)">Master Degree (PG)</option>
                  <option value="Doctorate (Ph.D)">Doctorate (Ph.D)</option>
                  <option value="Certification / Other">Certification / Other</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Degree Name (e.g. B.Com, B.Tech)</label>
                <input
                  type="text"
                  value={form.degree}
                  onChange={e => setForm({ ...form, degree: e.target.value })}
                  placeholder="e.g. B.Com / B.E."
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-edu-degree"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Institution / College Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.institution}
                  onChange={e => setForm({ ...form, institution: e.target.value })}
                  placeholder="e.g. St. Joseph College"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-edu-institution"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">University / Board</label>
                <input
                  type="text"
                  value={form.university}
                  onChange={e => setForm({ ...form, university: e.target.value })}
                  placeholder="e.g. University of Madras / State Board"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-edu-university"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Specialisation / Major</label>
                <input
                  type="text"
                  value={form.specialisation}
                  onChange={e => setForm({ ...form, specialisation: e.target.value })}
                  placeholder="e.g. Computer Science / Commerce"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-edu-specialisation"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Year of Passing <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.year_of_passing}
                  onChange={e => setForm({ ...form, year_of_passing: e.target.value })}
                  placeholder="e.g. 2022"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-edu-year"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Percentage / CGPA</label>
                <input
                  type="text"
                  value={form.percentage}
                  onChange={e => setForm({ ...form, percentage: e.target.value })}
                  placeholder="e.g. 82% or 8.5 CGPA"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-edu-percentage"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="button" size="sm" onClick={saveModal} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">Save Record</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Multi-Record Previous Employer Component
// ---------------------------------------------------------------------------
const PreviousEmployerSection = ({ data, setData, readOnly }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [form, setForm] = useState({
    employer_name: '', designation: '', department: '', employment_type: 'Full Time',
    from_date: '', to_date: '', experience: '', salary: '', reason: '', manager: '',
  });

  const list = Array.isArray(data.previous_employers) ? data.previous_employers : [];
  const isFresher = !!data.is_fresher;

  const handleDateChange = (field, val) => {
    const nextForm = { ...form, [field]: val };
    const calculatedExp = calculateExperience(nextForm.from_date, nextForm.to_date);
    nextForm.experience = calculatedExp;
    setForm(nextForm);
  };

  const openAdd = () => {
    setForm({
      employer_name: '', designation: '', department: '', employment_type: 'Full Time',
      from_date: '', to_date: '', experience: '', salary: '', reason: '', manager: '',
    });
    setEditingIndex(null);
    setShowModal(true);
  };

  const openEdit = (idx) => {
    const item = { ...list[idx] };
    item.experience = calculateExperience(item.from_date, item.to_date) || item.experience || '';
    setForm(item);
    setEditingIndex(idx);
    setShowModal(true);
  };

  const removeItem = (idx) => {
    if (!window.confirm('Delete this previous employer record?')) return;
    const nextList = list.filter((_, i) => i !== idx);
    const lastItem = nextList[nextList.length - 1] || {};
    setData(prev => ({
      ...prev,
      previous_employers: nextList,
      prev_employer_name: lastItem.employer_name || '',
      prev_designation: lastItem.designation || '',
      prev_from: lastItem.from_date || '',
      prev_to: lastItem.to_date || '',
      prev_reason: lastItem.reason || '',
    }));
  };

  const saveModal = () => {
    if (!form.employer_name.trim() || form.employer_name.trim().length < 2) {
      toast.error('Employer Name must be at least 2 characters.');
      return;
    }
    if (!form.designation.trim() || form.designation.trim().length < 2) {
      toast.error('Designation must be at least 2 characters.');
      return;
    }
    if (!form.from_date || !form.to_date) {
      toast.error('From Date and To Date are required.');
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (form.from_date > today) {
      toast.error('From Date cannot be a future date.');
      return;
    }
    if (form.to_date < form.from_date) {
      toast.error('To Date cannot be before From Date.');
      return;
    }
    if (form.to_date > today) {
      toast.error('To Date cannot be a future date.');
      return;
    }
    if (!form.reason.trim() || form.reason.trim().length < 5) {
      toast.error('Reason for Leaving must be at least 5 characters.');
      return;
    }

    const calculatedExp = calculateExperience(form.from_date, form.to_date);
    const updatedForm = { ...form, experience: calculatedExp };

    const nextList = [...list];
    if (editingIndex !== null) {
      nextList[editingIndex] = updatedForm;
    } else {
      nextList.push(updatedForm);
    }

    const latest = nextList[nextList.length - 1] || {};
    setData(prev => ({
      ...prev,
      previous_employers: nextList,
      prev_employer_name: latest.employer_name || '',
      prev_designation: latest.designation || '',
      prev_from: latest.from_date || '',
      prev_to: latest.to_date || '',
      prev_reason: latest.reason || '',
    }));
    setShowModal(false);
  };

  const toggleFresher = (checked) => {
    setData(prev => ({
      ...prev,
      is_fresher: checked,
      previous_employers: checked ? [] : prev.previous_employers || [],
    }));
  };

  return (
    <div className="space-y-4" data-testid="previous-employer-multi-section">
      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Briefcase className="w-4 h-4 text-[#0052FF]" /> Previous Employer Details ({isFresher ? 'Fresher' : list.length})
          </h3>
          <p className="text-[11px] text-slate-500">Record all prior work experiences and organizations</p>
        </div>
        <div className="flex items-center gap-3">
          {!readOnly && (
            <label className="flex items-center gap-1.5 text-xs text-blue-800 font-medium cursor-pointer select-none bg-blue-50 px-2.5 py-1 rounded border border-blue-200">
              <input
                type="checkbox"
                checked={isFresher}
                onChange={e => toggleFresher(e.target.checked)}
                data-testid="cb-is-fresher-multi"
                className="h-3.5 w-3.5 rounded text-[#0052FF]"
              />
              I am a Fresher
            </label>
          )}
          {!readOnly && !isFresher && (
            <Button
              type="button"
              size="sm"
              onClick={openAdd}
              data-testid="btn-add-employer"
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white text-xs flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add Employer
            </Button>
          )}
        </div>
      </div>

      {isFresher ? (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-900 flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>Marked as <b>Fresher</b> — No prior work experience records required.</span>
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-slate-200 rounded-lg bg-slate-50">
          <Briefcase className="w-8 h-8 mx-auto text-slate-300 mb-1" />
          <p className="text-xs text-slate-500 font-medium">No previous employer records added yet.</p>
          {!readOnly && <p className="text-[11px] text-slate-400 mt-0.5">Click "+ Add Employer" above to add your employment history.</p>}
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 text-slate-700 font-semibold uppercase border-b border-slate-200">
              <tr>
                <th className="p-2.5">Employer Name</th>
                <th className="p-2.5">Designation</th>
                <th className="p-2.5">Dept / Type</th>
                <th className="p-2.5">From - To Date</th>
                <th className="p-2.5">Calculated Experience</th>
                <th className="p-2.5">Last Salary</th>
                <th className="p-2.5">Reason for Leaving</th>
                {!readOnly && <th className="p-2.5 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((item, idx) => {
                const expVal = calculateExperience(item.from_date, item.to_date) || item.experience || '—';
                return (
                  <tr key={idx} className="hover:bg-slate-50/80">
                    <td className="p-2.5 font-semibold text-slate-900">{item.employer_name || '—'}</td>
                    <td className="p-2.5 text-slate-800">{item.designation || '—'}</td>
                    <td className="p-2.5 text-slate-600">{item.department || item.employment_type || '—'}</td>
                    <td className="p-2.5 font-mono text-slate-700">{item.from_date} to {item.to_date}</td>
                    <td className="p-2.5 font-semibold text-[#0052FF]">{expVal}</td>
                    <td className="p-2.5 font-mono text-slate-800">{item.salary ? `₹ ${item.salary}` : '—'}</td>
                    <td className="p-2.5 text-slate-700">{item.reason || '—'}</td>
                    {!readOnly && (
                      <td className="p-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(idx)}
                            data-testid={`edit-employer-${idx}`}
                            className="p-1 text-slate-500 hover:text-[#0052FF] hover:bg-blue-50 rounded"
                            title="Edit Record"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            data-testid={`delete-employer-${idx}`}
                            className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete Record"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Dialog for Add / Edit Previous Employer */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="modal-employer-form">
          <div className="bg-white rounded-xl w-full max-w-xl overflow-hidden shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50/80 sticky top-0 bg-white z-10">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-[#0052FF]" />
                {editingIndex !== null ? 'Edit Employer Record' : 'Add Employer Record'}
              </h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Employer / Company Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.employer_name}
                  onChange={e => setForm({ ...form, employer_name: e.target.value })}
                  placeholder="e.g. ABC Technologies Pvt Ltd"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-emp-name"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Designation <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.designation}
                  onChange={e => setForm({ ...form, designation: e.target.value })}
                  placeholder="e.g. Software Engineer"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-emp-designation"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Department</label>
                <input
                  type="text"
                  value={form.department}
                  onChange={e => setForm({ ...form, department: e.target.value })}
                  placeholder="e.g. Information Technology"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-emp-department"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Employment Type</label>
                <select
                  value={form.employment_type}
                  onChange={e => setForm({ ...form, employment_type: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-emp-type"
                >
                  <option value="Full Time">Full Time</option>
                  <option value="Part Time">Part Time</option>
                  <option value="Contract">Contract</option>
                  <option value="Internship">Internship</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Last Drawn Salary (Annual / Monthly)</label>
                <input
                  type="text"
                  value={form.salary}
                  onChange={e => setForm({ ...form, salary: e.target.value })}
                  placeholder="e.g. 5,50,000"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-emp-salary"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">From Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={form.from_date}
                  onChange={e => handleDateChange('from_date', e.target.value)}
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-emp-from"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">To Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={form.to_date}
                  onChange={e => handleDateChange('to_date', e.target.value)}
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-emp-to"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-semibold text-blue-900 mb-1">Total Experience (Auto-Calculated)</label>
                <input
                  type="text"
                  value={form.experience}
                  readOnly
                  disabled
                  placeholder="Select From and To dates to calculate experience"
                  className="w-full border border-blue-200 bg-blue-50 font-semibold text-blue-800 rounded px-2.5 py-1.5 text-xs cursor-not-allowed"
                  data-testid="input-emp-experience"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Reason for Leaving <span className="text-red-500">*</span></label>
                <textarea
                  rows={2}
                  value={form.reason}
                  onChange={e => setForm({ ...form, reason: e.target.value })}
                  placeholder="Reason for leaving this job (min 5 characters)"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-emp-reason"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Reporting Manager (Optional)</label>
                <input
                  type="text"
                  value={form.manager}
                  onChange={e => setForm({ ...form, manager: e.target.value })}
                  placeholder="Name / Contact of Reporting Manager"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-emp-manager"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 sticky bottom-0 bg-slate-50 z-10">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="button" size="sm" onClick={saveModal} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">Save Record</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Health Insurance Family Details Component
// ---------------------------------------------------------------------------
export function calculateAge(dobStr) {
  if (!dobStr) return '';
  const birth = new Date(dobStr);
  if (isNaN(birth.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age >= 0 ? `${age} Yrs` : '';
}

const FamilyDetailsSection = ({ data, setData, readOnly }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [form, setForm] = useState({
    name: '', relationship: 'Spouse', gender: 'Female', date_of_birth: '',
    mobile: '', aadhaar_number: '', blood_group: 'O+', occupation: 'Housewife',
    is_dependent: true, is_nominee: false,
  });

  const members = Array.isArray(data.family_members) ? data.family_members : [];

  const openAdd = () => {
    setForm({
      name: '', relationship: 'Spouse', gender: 'Female', date_of_birth: '',
      mobile: '', aadhaar_number: '', blood_group: 'O+', occupation: 'Housewife',
      is_dependent: true, is_nominee: false,
    });
    setEditingIndex(null);
    setShowModal(true);
  };

  const openEdit = (idx) => {
    setForm({ ...members[idx] });
    setEditingIndex(idx);
    setShowModal(true);
  };

  const removeItem = (idx) => {
    if (!window.confirm('Remove this family member from health insurance declaration?')) return;
    const nextList = members.filter((_, i) => i !== idx);
    setData(prev => ({
      ...prev,
      family_members: nextList,
    }));
  };

  const saveModal = () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters.');
      return;
    }
    if (!form.relationship) {
      toast.error('Please select Relationship.');
      return;
    }
    if (!form.gender) {
      toast.error('Please select Gender.');
      return;
    }
    if (!form.date_of_birth) {
      toast.error('Please enter Date of Birth.');
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (form.date_of_birth > today) {
      toast.error('Date of Birth cannot be a future date.');
      return;
    }

    if (form.aadhaar_number) {
      const cleanAadhaar = form.aadhaar_number.replace(/\s+/g, '');
      if (!/^\d{12}$/.test(cleanAadhaar)) {
        toast.error('Aadhaar Number must be exactly 12 digits.');
        return;
      }
      const duplicateIdx = members.findIndex((m, i) => i !== editingIndex && (m.aadhaar_number || '').replace(/\s+/g, '') === cleanAadhaar);
      if (duplicateIdx !== -1) {
        toast.error(`Duplicate Aadhaar Number '${cleanAadhaar}' already entered for ${members[duplicateIdx].name}.`);
        return;
      }
    }

    const nextList = [...members];
    if (editingIndex !== null) {
      nextList[editingIndex] = form;
    } else {
      nextList.push(form);
    }

    setData(prev => ({
      ...prev,
      family_members: nextList,
    }));
    setShowModal(false);
  };

  return (
    <div className="space-y-4" data-testid="family-details-section">
      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <UsersIcon className="w-4 h-4 text-[#0052FF]" /> Covered Family Members &amp; Dependents ({members.length})
          </h3>
          <p className="text-[11px] text-slate-500">Maintain family information to be covered under your Health Insurance policy.</p>
        </div>
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            onClick={openAdd}
            data-testid="btn-add-family-member"
            className="bg-[#0052FF] hover:bg-[#0043D1] text-white text-xs flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Add Family Member
          </Button>
        )}
      </div>

      {members.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-slate-200 rounded-lg bg-slate-50">
          <UsersIcon className="w-8 h-8 mx-auto text-slate-300 mb-1" />
          <p className="text-xs text-slate-500 font-medium">No family members added yet.</p>
          {!readOnly && <p className="text-[11px] text-slate-400 mt-0.5">Click "+ Add Family Member" above to include dependents.</p>}
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg shadow-sm">
          <table className="w-full text-xs text-left" data-testid="family-members-table">
            <thead className="bg-slate-50 text-slate-700 font-semibold uppercase border-b border-slate-200">
              <tr>
                <th className="p-2.5">Name</th>
                <th className="p-2.5">Relationship</th>
                <th className="p-2.5">Gender</th>
                <th className="p-2.5">DOB</th>
                <th className="p-2.5">Age</th>
                <th className="p-2.5">Mobile</th>
                <th className="p-2.5">Aadhaar</th>
                <th className="p-2.5">Blood Group</th>
                <th className="p-2.5">Occupation</th>
                <th className="p-2.5 text-center">Dependent</th>
                <th className="p-2.5 text-center">Nominee</th>
                {!readOnly && <th className="p-2.5 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {members.map((m, idx) => (
                <tr key={idx} className="hover:bg-slate-50/80">
                  <td className="p-2.5 font-semibold text-slate-900">{m.name}</td>
                  <td className="p-2.5 text-slate-800 font-medium">{m.relationship}</td>
                  <td className="p-2.5 text-slate-700">{m.gender}</td>
                  <td className="p-2.5 font-mono text-slate-700">{m.date_of_birth || '—'}</td>
                  <td className="p-2.5 font-semibold text-[#0052FF]">{calculateAge(m.date_of_birth) || '—'}</td>
                  <td className="p-2.5 font-mono text-slate-600">{m.mobile || '—'}</td>
                  <td className="p-2.5 font-mono text-slate-600">{m.aadhaar_number || '—'}</td>
                  <td className="p-2.5 text-slate-700">{m.blood_group || '—'}</td>
                  <td className="p-2.5 text-slate-600">{m.occupation || '—'}</td>
                  <td className="p-2.5 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.is_dependent !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
                      {m.is_dependent !== false ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="p-2.5 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.is_nominee ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-500'}`}>
                      {m.is_nominee ? 'Yes' : 'No'}
                    </span>
                  </td>
                  {!readOnly && (
                    <td className="p-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(idx)}
                          data-testid={`edit-family-${idx}`}
                          className="p-1 text-slate-500 hover:text-[#0052FF] hover:bg-blue-50 rounded"
                          title="Edit Member"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          data-testid={`delete-family-${idx}`}
                          className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete Member"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Dialog for Add / Edit Family Member */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="modal-family-form">
          <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden shadow-2xl space-y-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50/80">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <UsersIcon className="w-4 h-4 text-[#0052FF]" />
                {editingIndex !== null ? 'Edit Family Member' : 'Add Family Member'}
              </h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Jane Doe"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-family-name"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Relationship <span className="text-red-500">*</span></label>
                <select
                  value={form.relationship}
                  onChange={e => setForm({ ...form, relationship: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs focus:ring-[#0052FF]"
                  data-testid="select-family-relationship"
                >
                  <option value="Spouse">Spouse</option>
                  <option value="Son">Son</option>
                  <option value="Daughter">Daughter</option>
                  <option value="Child">Child</option>
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Father-in-law">Father-in-law</option>
                  <option value="Mother-in-law">Mother-in-law</option>
                  <option value="Brother">Brother</option>
                  <option value="Sister">Sister</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Gender <span className="text-red-500">*</span></label>
                <select
                  value={form.gender}
                  onChange={e => setForm({ ...form, gender: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="select-family-gender"
                >
                  <option value="Female">Female</option>
                  <option value="Male">Male</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Date of Birth <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={form.date_of_birth}
                  onChange={e => setForm({ ...form, date_of_birth: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-family-dob"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Calculated Age</label>
                <input
                  type="text"
                  readOnly
                  value={calculateAge(form.date_of_birth) || '—'}
                  className="w-full border border-slate-200 bg-slate-100 rounded px-2.5 py-1.5 text-xs font-semibold text-[#0052FF]"
                  data-testid="input-family-calculated-age"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Mobile Number</label>
                <input
                  type="text"
                  value={form.mobile}
                  onChange={e => setForm({ ...form, mobile: e.target.value })}
                  placeholder="e.g. 9876543210"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-family-mobile"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Aadhaar Number (Optional)</label>
                <input
                  type="text"
                  value={form.aadhaar_number}
                  onChange={e => setForm({ ...form, aadhaar_number: e.target.value })}
                  placeholder="12 digits"
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="input-family-aadhaar"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Blood Group</label>
                <select
                  value={form.blood_group}
                  onChange={e => setForm({ ...form, blood_group: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="select-family-blood-group"
                >
                  <option value="O+">O+</option>
                  <option value="A+">A+</option>
                  <option value="B+">B+</option>
                  <option value="AB+">AB+</option>
                  <option value="O-">O-</option>
                  <option value="A-">A-</option>
                  <option value="B-">B-</option>
                  <option value="AB-">AB-</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Occupation</label>
                <select
                  value={form.occupation}
                  onChange={e => setForm({ ...form, occupation: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-xs"
                  data-testid="select-family-occupation"
                >
                  <option value="Housewife">Housewife</option>
                  <option value="Service">Service / Employee</option>
                  <option value="Business">Business</option>
                  <option value="Student">Student</option>
                  <option value="Retired">Retired</option>
                  <option value="Self-Employed">Self-Employed</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="sm:col-span-2 flex items-center gap-6 pt-1 border-t border-slate-100 mt-2">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!form.is_dependent}
                    onChange={e => setForm({ ...form, is_dependent: e.target.checked })}
                    className="h-4 w-4 rounded text-[#0052FF] border-slate-300"
                    data-testid="cb-family-dependent"
                  />
                  Mark as Dependent
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!form.is_nominee}
                    onChange={e => setForm({ ...form, is_nominee: e.target.checked })}
                    className="h-4 w-4 rounded text-[#0052FF] border-slate-300"
                    data-testid="cb-family-nominee"
                  />
                  Mark as Policy Nominee
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="button" size="sm" onClick={saveModal} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">Save Member</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Frontend validation helper
// ---------------------------------------------------------------------------
function validateJoiningForm(data) {
  const errors = {};
  const currentYear = new Date().getFullYear();

  // ── Education array validation ──
  const eduList = Array.isArray(data.education) ? data.education : [];
  if (eduList.length === 0) {
    const EDU_KEYS = ['highest_qualification', 'institution', 'year_of_passing', 'specialisation'];
    const eduFilled = EDU_KEYS.filter(k => String(data[k] || '').trim());
    if (eduFilled.length > 0 && eduFilled.length < EDU_KEYS.length) {
      EDU_KEYS.forEach(k => {
        if (!String(data[k] || '').trim()) errors[k] = `Required field missing in education details.`;
      });
    }
  } else {
    eduList.forEach((item, idx) => {
      if (!String(item.qualification || item.degree || '').trim()) {
        errors.highest_qualification = `Education #${idx + 1}: Qualification / Degree is required.`;
      }
      if (!String(item.institution || '').trim()) {
        errors.highest_qualification = `Education #${idx + 1}: Institution is required.`;
      }
      if (!item.year_of_passing || !/^\d{4}$/.test(String(item.year_of_passing).trim())) {
        errors.year_of_passing = `Education #${idx + 1}: Year of Passing must be a 4-digit year.`;
      } else {
        const yr = parseInt(item.year_of_passing, 10);
        if (yr < 1950 || yr > currentYear) {
          errors.year_of_passing = `Education #${idx + 1}: Year of Passing must be between 1950 and ${currentYear}.`;
        }
      }
    });
  }

  // ── Previous Employer array validation ──
  if (!data.is_fresher) {
    const empList = Array.isArray(data.previous_employers) ? data.previous_employers : [];
    if (empList.length === 0) {
      const PREV_KEYS = ['prev_employer_name', 'prev_designation', 'prev_from', 'prev_to', 'prev_reason'];
      const prevFilled = PREV_KEYS.filter(k => String(data[k] || '').trim());
      if (prevFilled.length > 0) {
        PREV_KEYS.forEach(k => {
          if (!String(data[k] || '').trim()) errors[k] = `Previous Employer field required.`;
        });
      }
    } else {
      const today = new Date().toISOString().slice(0, 10);
      empList.forEach((item, idx) => {
        if (!String(item.employer_name || '').trim() || String(item.employer_name).trim().length < 2) {
          errors.prev_employer_name = `Employer #${idx + 1}: Employer Name must be at least 2 characters.`;
        }
        if (!String(item.designation || '').trim() || String(item.designation).trim().length < 2) {
          errors.prev_designation = `Employer #${idx + 1}: Designation must be at least 2 characters.`;
        }
        if (!item.from_date) errors.prev_from = `Employer #${idx + 1}: From Date is required.`;
        if (!item.to_date) errors.prev_to = `Employer #${idx + 1}: To Date is required.`;
        if (item.from_date && item.from_date > today) errors.prev_from = `Employer #${idx + 1}: From Date cannot be a future date.`;
        if (item.to_date && item.from_date && item.to_date < item.from_date) errors.prev_to = `Employer #${idx + 1}: To Date cannot be before From Date.`;
        if (item.to_date && item.to_date > today) errors.prev_to = `Employer #${idx + 1}: To Date cannot be a future date.`;
        if (!String(item.reason || '').trim() || String(item.reason).trim().length < 5) {
          errors.prev_reason = `Employer #${idx + 1}: Reason for Leaving must be at least 5 characters.`;
        }
      });
    }
  }

  // ── Bank Details ──
  const acct = String(data.account_number || '').replace(/\s+/g, '');
  if (!String(data.bank_name || '').trim()) errors.bank_name = 'Bank Name is required.';
  if (!acct) {
    errors.account_number = 'Account Number is required.';
  } else if (!/^\d+$/.test(acct)) {
    errors.account_number = 'Account Number must contain digits only.';
  } else if (acct.length < 9 || acct.length > 18) {
    errors.account_number = 'Account Number must be 9–18 digits.';
  }
  const ifsc = String(data.ifsc_code || '').trim().toUpperCase();
  if (!ifsc) {
    errors.ifsc_code = 'IFSC Code is required.';
  } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
    errors.ifsc_code = 'IFSC Code is invalid (e.g. SBIN0001234).';
  }
  if (!String(data.bank_branch || '').trim()) errors.bank_branch = 'Branch is required.';
  else if (String(data.bank_branch).trim().length < 2) errors.bank_branch = 'Branch must be at least 2 characters.';

  // ── PAN ──
  const pan = String(data.pan_number || '').trim().toUpperCase();
  if (pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan)) errors.pan_number = 'PAN is invalid (e.g. ABCDE1234F).';

  // ── Aadhaar ──
  const aadhaar = String(data.aadhaar_number || '').replace(/\s+/g, '');
  if (aadhaar && (!/^\d+$/.test(aadhaar) || aadhaar.length !== 12)) errors.aadhaar_number = 'Aadhaar must be exactly 12 digits.';

  // ── Mobile ──
  const mobile = String(data.mobile || '').replace(/\s+/g, '');
  if (mobile && !/^[6-9]\d{9}$/.test(mobile)) errors.mobile = 'Mobile must be 10 digits starting with 6–9.';

  return errors;
}

export default function ESSStatutoryForms({ initialTab }) {
  const [tab, setTab] = useState(initialTab || 'joining');
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});         // current tab form data
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});  // inline validation errors
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [profilePhotoDetails, setProfilePhotoDetails] = useState(null);
  const [fullProfile, setFullProfile] = useState(null);
  const [policyConfig, setPolicyConfig] = useState(null);

  // Webcam capture state & refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [showCameraModal, setShowCameraModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      setTab(tabParam);
    } else if (initialTab) {
      setTab(initialTab);
    }
  }, [initialTab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [formsRes, profileRes, policyRes] = await Promise.all([
        axios.get(`${API_URL}/ess/statutory-forms`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/profile/full`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/ess/health-insurance-policy`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setForms(Array.isArray(formsRes.data) ? formsRes.data : []);
      if (profileRes.data) {
        setFullProfile(profileRes.data);
        setEmployeeId(profileRes.data.employee_id || '');
        setProfilePhotoDetails(profileRes.data.personal_details || {});
      }
      if (policyRes.data) {
        setPolicyConfig(policyRes.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load statutory forms or profile details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentForm = forms.find(f => f.form_type === tab);
  useEffect(() => {
    if (tab === 'joining' && profilePhotoDetails) {
      setData(prev => {
        const formD = currentForm?.data || {};
        if (!formD.photoUrl && profilePhotoDetails.photoUrl) {
          return {
            ...formD,
            profilePhoto: profilePhotoDetails.profilePhoto,
            photoUrl: profilePhotoDetails.photoUrl,
            photoThumbnail: profilePhotoDetails.photoThumbnail,
            uploadedAt: profilePhotoDetails.uploadedAt,
            uploadedBy: profilePhotoDetails.uploadedBy,
          };
        }
        return formD;
      });
    } else if (tab === 'health_insurance') {
      const formD = currentForm?.data || {};
      const pd = fullProfile?.personal_details || {};
      const ed = fullProfile?.employment_details || {};
      const sd = fullProfile?.salary_details || {};
      const empName = `${pd.first_name || ''} ${pd.last_name || ''}`.trim() || 'Employee';
      const grossSal = sd.gross_monthly || sd.gross_salary || sd.gross || fullProfile?.gross_salary || fullProfile?.gross_monthly || 0;

      // Company assigned policy sum insured (Always read-only from company policy)
      const companySumInsured = policyConfig?.sum_insured || policyConfig?.default_sum_insured || "5,00,000";

      // Auto-load family members from profile nominees if not set yet in draft formD
      let familyMembers = formD.family_members || formD.nominees || [];
      if (!familyMembers.length && fullProfile?.nominees?.length) {
        familyMembers = fullProfile.nominees.map(n => ({
          name: n.name || n.nominee_name || '',
          relationship: n.relationship || n.nominee_relationship || 'Spouse',
          date_of_birth: n.date_of_birth || '',
          gender: n.gender || 'Female',
          mobile: n.contact_number || n.mobile || '',
          aadhaar_number: n.aadhaar_number || '',
          blood_group: n.blood_group || 'O+',
          occupation: n.occupation || 'Other',
          is_dependent: true,
          is_nominee: true,
        }));
      }

      setData({
        policy_type: formD.policy_type || policyConfig?.policy_type || 'Self + Family',
        sum_insured: companySumInsured,
        top_up_opted: formD.top_up_opted || 'No',
        top_up_amount: formD.top_up_amount || '',
        parental_cover: formD.parental_cover || 'No',
        // Auto-populated Self Details
        employee_id: fullProfile?.employee_id || '',
        employee_code: ed.employee_code || '',
        employee_name: empName,
        company_name: ed.company_name || 'Hubert Enviro Care Systems',
        gender: pd.gender || '',
        date_of_birth: pd.date_of_birth || '',
        mobile: pd.mobile || '',
        email: ed.work_email || pd.personal_email || '',
        blood_group: pd.blood_group || '',
        marital_status: pd.marital_status || '',
        department_name: ed.department_name || '',
        designation_name: ed.designation_name || '',
        date_of_joining: ed.date_of_joining || '',
        location_name: ed.location_name || '',
        branch_name: ed.location_name || '',
        address: pd.current_address || pd.permanent_address || '',
        gross_salary: grossSal,
        family_members: familyMembers,
        ...formD,
      });
    } else if (tab === 'esi_application' || tab === 'esi') {
      const formD = currentForm?.data || {};
      const pd = fullProfile?.personal_details || {};
      const ed = fullProfile?.employment_details || {};
      const sd = fullProfile?.salary_details || {};
      const ks = fullProfile?.kyc_statutory_details || {};
      const empName = `${pd.first_name || ''} ${pd.last_name || ''}`.trim() || 'Employee';
      const basicSal = sd.basic_monthly || sd.basic || fullProfile?.basic_salary || 0;
      const grossSal = sd.gross_monthly || sd.gross_salary || sd.gross || fullProfile?.gross_salary || fullProfile?.gross_monthly || 0;

      let familyMembers = formD.family_members || formD.nominees || [];
      if (!familyMembers.length && fullProfile?.nominees?.length) {
        familyMembers = fullProfile.nominees.map(n => ({
          name: n.name || n.nominee_name || '',
          relationship: n.relationship || n.nominee_relationship || 'Spouse',
          date_of_birth: n.date_of_birth || '',
          gender: n.gender || 'Female',
          mobile: n.contact_number || n.mobile || '',
          aadhaar_number: n.aadhaar_number || '',
          blood_group: n.blood_group || 'O+',
          occupation: n.occupation || 'Other',
          is_dependent: true,
          is_nominee: true,
        }));
      }

      setData({
        employee_id: fullProfile?.employee_id || '',
        employee_code: ed.employee_code || '',
        employee_name: empName,
        father_or_husband_name: pd.father_name || pd.father_or_husband_name || '',
        gender: pd.gender || '',
        date_of_birth: pd.date_of_birth || '',
        mobile: pd.mobile || '',
        email: pd.personal_email || ed.work_email || '',
        marital_status: pd.marital_status || '',
        company_name: ed.company_name || 'Hubert Enviro Care Systems',
        department_name: ed.department_name || '',
        designation_name: ed.designation_name || '',
        date_of_joining: ed.date_of_joining || '',
        location_name: ed.location_name || '',
        branch_name: ed.location_name || '',
        address: pd.current_address || pd.permanent_address || '',
        basic_salary: basicSal,
        gross_salary: grossSal,
        esi_number: formD.esi_number || ks.esi_number || '',
        prev_esi_number: formD.prev_esi_number || '',
        dispensary_name: formD.dispensary_name || '',
        esi_doctor_name: formD.esi_doctor_name || '',
        family_members: familyMembers,
        ...formD,
      });
    } else if (tab === 'acknowledgement') {
      const formD = currentForm?.data || {};
      const pd = fullProfile?.personal_details || {};
      const ed = fullProfile?.employment_details || {};
      const empName = `${pd.first_name || ''} ${pd.last_name || ''}`.trim() || 'Employee';
      const currentDate = new Date().toISOString().slice(0, 10);
      setData({
        document_title: 'HECS Employee HR Policy Manual',
        manual_date: formD.manual_date || ed.date_of_joining || '01-Jan-2026',
        employee_name: formD.employee_name || empName,
        employee_code: formD.employee_code || ed.employee_code || '',
        department_name: formD.department_name || ed.department_name || '',
        designation_name: formD.designation_name || ed.designation_name || '',
        location_name: formD.location_name || ed.location_name || '',
        date_of_joining: formD.date_of_joining || ed.date_of_joining || '',
        company_name: formD.company_name || ed.company_name || 'Hubert Enviro Care Systems',
        business_unit: formD.business_unit || ed.business_unit || 'HECS Main Unit',
        work_email: formD.work_email || ed.work_email || pd.personal_email || '',
        mobile: formD.mobile || pd.mobile || '',
        reporting_manager_name: formD.reporting_manager_name || ed.reporting_manager_name || 'HR Manager',
        employment_status: formD.employment_status || ed.employment_status || 'Active',
        accepted_terms: formD.accepted_terms ?? false,
        digital_signature: formD.digital_signature || '',
        confirmation_date: formD.confirmation_date || currentDate,
        ...formD,
      });
    } else {
      setData(currentForm?.data || {});
    }
  }, [tab, currentForm?.data, profilePhotoDetails, fullProfile]);

  const readOnly = currentForm && ['submitted', 'approved'].includes(currentForm.status);

  const startCamera = async () => {
    setCameraError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 480, height: 480, facingMode: 'user' }, audio: false,
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      setCameraError('Camera access denied. Please allow camera permission.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext('2d');
    ctx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], "captured_photo.jpg", { type: "image/jpeg" });
      await handlePhotoUpload(file);
      setShowCameraModal(false);
      stopCamera();
    }, 'image/jpeg', 0.85);
  };

  const handlePhotoUpload = async (file) => {
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPG, JPEG, PNG, and WEBP formats are allowed.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Maximum file size is 2 MB.');
      return;
    }
    if (!employeeId) {
      toast.error("Employee must be saved before uploading a photo.");
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data: uploadData } = await axios.post(`${API_URL}/employees/${employeeId}/photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setField('profilePhoto', uploadData.profilePhoto);
      setField('photoUrl', uploadData.photoUrl);
      setField('photoThumbnail', uploadData.photoThumbnail);
      setField('uploadedAt', uploadData.uploadedAt);
      setField('uploadedBy', uploadData.uploadedBy);
      toast.success('Passport size photo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload photo');
    }
  };

  const handlePhotoRemove = async () => {
    if (!window.confirm('Are you sure you want to remove the photo?')) return;
    if (!employeeId) {
      toast.error("Employee must be saved before uploading a photo.");
      return;
    }
    try {
      await axios.delete(`${API_URL}/employees/${employeeId}/photo`, { withCredentials: true });
      setField('profilePhoto', null);
      setField('photoUrl', null);
      setField('photoThumbnail', null);
      setField('uploadedAt', null);
      setField('uploadedBy', null);
      toast.success('Photo removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const setField = (key, value) => {
    setData(prev => {
      const next = { ...prev, [key]: value };
      if (submitAttempted && tab === 'joining') {
        setFieldErrors(validateJoiningForm(next));
      }
      return next;
    });
  };

  const setRepeaterRow = (repKey, idx, key, value) => {
    setData(prev => {
      const list = Array.isArray(prev[repKey]) ? [...prev[repKey]] : [];
      list[idx] = { ...(list[idx] || {}), [key]: value };
      return { ...prev, [repKey]: list };
    });
  };
  const addRepeaterRow = (repKey) => setData(prev => ({ ...prev, [repKey]: [...(prev[repKey] || []), {}] }));
  const removeRepeaterRow = (repKey, idx) => setData(prev => ({
    ...prev, [repKey]: (prev[repKey] || []).filter((_, i) => i !== idx),
  }));

  const save = async (submit = false) => {
    if (tab === 'esi_application' && !isEsiEligible) {
      toast.error('ESI is not applicable because your Gross Salary is above ₹21,000.');
      return;
    }
    if (tab === 'health_insurance' && !isHealthInsuranceEligible) {
      toast.error('Health Insurance is not applicable because your Gross Salary is ₹21,000 or below.');
      return;
    }

    if (submit && tab === 'acknowledgement') {
      if (!data.accepted_terms) {
        toast.error('You must check the confirmation box agreeing to the Employee Handbook.');
        return;
      }
      if (!data.digital_signature) {
        toast.error('Please provide your digital signature before submitting.');
        return;
      }
    }

    // ── Joining form client-side validation ──────────────────────────────
    if (submit && tab === 'joining') {
      setSubmitAttempted(true);
      const errs = validateJoiningForm(data);
      setFieldErrors(errs);
      if (Object.keys(errs).length > 0) {
        toast.error('Please fix the highlighted errors before submitting.');
        // Auto-focus the first invalid field
        const firstKey = Object.keys(errs)[0];
        const el = document.querySelector(`[data-testid="form-field-${firstKey}"]`);
        if (el) el.focus();
        return;
      }
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/ess/statutory-forms/${tab}`,
        { data, submit },
        { withCredentials: true },
      );
      toast.success(submit ? 'Submitted to HR for review' : 'Draft saved');
      setFieldErrors({});
      setSubmitAttempted(false);
      load();
    } catch (e) {
      // Surface server-side 422 validation errors inline
      const detail = e.response?.data?.detail;
      if (e.response?.status === 422 && detail?.errors) {
        const serverErrs = {};
        detail.errors.forEach(msg => {
          // Map server error messages to field keys
          if (msg.includes('PAN')) serverErrs.pan_number = msg;
          else if (msg.includes('Aadhaar')) serverErrs.aadhaar_number = msg;
          else if (msg.includes('Account Number')) serverErrs.account_number = msg;
          else if (msg.includes('IFSC')) serverErrs.ifsc_code = msg;
          else if (msg.includes('Bank Name')) serverErrs.bank_name = msg;
          else if (msg.includes('Branch')) serverErrs.bank_branch = msg;
          else if (msg.includes('Mobile')) serverErrs.mobile = msg;
          else if (msg.includes('Year of Passing')) serverErrs.year_of_passing = msg;
          else if (msg.includes('Education')) {
            ['highest_qualification','institution','year_of_passing','specialisation'].forEach(k => { serverErrs[k] = msg; });
          } else if (msg.includes('Employer Name')) serverErrs.prev_employer_name = msg;
          else if (msg.includes('Designation')) serverErrs.prev_designation = msg;
          else if (msg.includes('From Date')) serverErrs.prev_from = msg;
          else if (msg.includes('To Date')) serverErrs.prev_to = msg;
          else if (msg.includes('Reason')) serverErrs.prev_reason = msg;
        });
        setFieldErrors(prev => ({ ...prev, ...serverErrs }));
        toast.error(detail.message || 'Validation failed. Please fix highlighted fields.');
      } else {
        toast.error(detail || e.response?.data?.detail || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!currentForm) return;
    try {
      const res = await axios.get(
        `${API_URL}/ess/statutory-forms/${currentForm.id}/pdf`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentForm.form_type}_${currentForm.employee_code || 'form'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF download failed');
    }
  };

  const currentMeta = FORM_META.find(m => m.key === tab);
  const schema = FIELD_SCHEMAS[tab] || [];

  const grossSalary = fullProfile?.salary_details?.gross_monthly || fullProfile?.salary_details?.gross_salary || fullProfile?.salary_details?.gross || fullProfile?.gross_salary || fullProfile?.gross_monthly || 0;
  const isEsiEligible = Number(grossSalary) <= 21000;
  const isHealthInsuranceEligible = Number(grossSalary) > 21000;

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="ess-statutory-forms-page">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Statutory Forms</h1>
          <p className="text-sm text-slate-600 mt-1">Fill out and submit — an HR administrator will review and approve.</p>
        </div>

        {/* Page Note Banner */}
        <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-3.5 text-xs text-blue-900 flex items-start gap-2 shadow-sm" data-testid="statutory-eligibility-note-banner">
          <AlertCircle className="w-4 h-4 text-[#0052FF] shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            <b>Statutory Eligibility Note:</b> Statutory eligibility is determined using the employee's <b>Gross Salary</b>. <b>ESI Application</b> is applicable for employees with Gross Salary up to <b>₹21,000</b>. <b>Health Insurance</b> is applicable for employees with Gross Salary above <b>₹21,000</b>.
          </p>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {FORM_META.map(m => {
            const Icon = m.icon;
            const active = tab === m.key;
            const my = forms.find(f => f.form_type === m.key);
            const isEsiTab = m.key === 'esi_application';
            const isHealthInsuranceTab = m.key === 'health_insurance';
            return (
              <button
                key={m.key}
                onClick={() => setTab(m.key)}
                data-testid={`tab-form-${m.key}`}
                title={m.tooltip || m.label}
                className={`text-left p-3 rounded-xl border transition ${active ? 'border-[#0052FF] bg-blue-50 shadow' : 'border-slate-200 bg-white hover:border-slate-300'}`}
              >
                <div className="flex items-center justify-between">
                  <Icon className={`w-5 h-5 ${active ? 'text-[#0052FF]' : 'text-slate-500'}`} />
                  {isEsiTab ? (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${isEsiEligible ? (my ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700') : 'bg-slate-100 text-slate-500'}`}>
                      {isEsiEligible ? (my ? <StatusBadge status={my.status} /> : 'Eligible') : 'Not Applicable'}
                    </span>
                  ) : isHealthInsuranceTab ? (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${isHealthInsuranceEligible ? (my ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700') : 'bg-slate-100 text-slate-500'}`}>
                      {isHealthInsuranceEligible ? (my ? <StatusBadge status={my.status} /> : 'Eligible') : 'Not Applicable'}
                    </span>
                  ) : (
                    my && <StatusBadge status={my.status} />
                  )}
                </div>
                <p className="font-semibold text-sm text-slate-900 mt-2">{m.label}</p>
                <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{m.desc}</p>
              </button>
            );
          })}
        </div>

        {/* Form Card */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`form-panel-${tab}`}>
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50/60">
            <div className="flex items-center gap-2">
              {currentMeta && <currentMeta.icon className="w-5 h-5 text-[#0052FF]" />}
              <h2 className="text-base font-semibold text-slate-800">{currentMeta?.label}</h2>
              {currentForm && <StatusBadge status={currentForm.status} />}
            </div>
            <div className="flex items-center gap-2">
              {currentForm?.id && (
                <button onClick={downloadPdf} data-testid="download-form-pdf"
                  className="text-xs text-[#0052FF] hover:underline flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <div className="p-5 space-y-6">
              {currentForm?.status === 'rejected' && currentForm.decision_remarks && (
                <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-md p-3">
                  <b>HR rejected this form.</b> Remarks: {currentForm.decision_remarks}. You can edit and re-submit.
                </div>
              )}
              {readOnly && currentForm?.status !== 'rejected' && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-md p-3">
                  This form is <b>{currentForm.status}</b>. It cannot be edited until HR unlocks it.
                </div>
              )}

              {tab === 'esi_application' ? (
                !isEsiEligible ? (
                  <div className="p-8 text-center space-y-4" data-testid="esi-not-eligible-view">
                    <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
                      <AlertCircle className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">ESI Application Not Applicable</h3>
                    <p className="text-xs text-slate-600 max-w-md mx-auto leading-relaxed">
                      ESI is not applicable because your Gross Salary (<b>₹{Number(grossSalary).toLocaleString('en-IN')}</b>) is above ₹21,000.
                    </p>
                    <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-3.5 py-1.5 rounded-lg text-xs font-semibold">
                      ESI Eligibility: Gross Salary ≤ ₹21,000
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6" data-testid="esi-application-form-section">
                    {/* Header Banner */}
                    <div className="bg-gradient-to-r from-teal-900 via-emerald-900 to-slate-900 text-white rounded-xl p-5 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                      <div>
                        <span className="bg-emerald-500/30 text-emerald-200 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-emerald-400/20">
                          Employee State Insurance (ESI)
                        </span>
                        <h3 className="text-xl font-bold mt-2">ESI Application</h3>
                        <p className="text-xs text-emerald-200 mt-0.5">ESI Eligibility: Gross Salary ≤ ₹21,000</p>
                      </div>
                      <div className="text-right text-xs text-slate-300">
                        <p><span className="text-slate-400">Gross Salary:</span> <b>₹{Number(grossSalary).toLocaleString('en-IN')}</b></p>
                        <p className="mt-1"><span className="text-slate-400">Status:</span> <b className="uppercase text-emerald-300">{currentForm?.status || 'Eligible (Draft)'}</b></p>
                      </div>
                    </div>

                    {/* 1. Employee Details (Auto-populated from Master) */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="esi-employee-details-card">
                      <div className="flex items-center justify-between mb-3 border-b border-slate-200/80 pb-2">
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                          <UsersIcon className="w-4 h-4 text-[#0052FF]" /> 1. Employee Details (Auto-populated from Employee Master)
                        </h4>
                        <span className="text-[10px] bg-blue-100 text-[#0052FF] font-bold px-2 py-0.5 rounded-full border border-blue-200">
                          Read-Only Master Link
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                        <div><span className="text-slate-400 block text-[10px]">Full Name</span><span className="font-semibold text-slate-800">{data.employee_name || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Employee Code</span><span className="font-semibold text-slate-800">{data.employee_code || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Date of Birth</span><span className="font-semibold text-slate-800">{data.date_of_birth || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Gender</span><span className="font-semibold text-slate-800">{data.gender || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Father's / Husband's Name</span><span className="font-semibold text-slate-800">{data.father_or_husband_name || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Marital Status</span><span className="font-semibold text-slate-800">{data.marital_status || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Mobile Number</span><span className="font-semibold text-slate-800">{data.mobile || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Personal Email</span><span className="font-semibold text-slate-800">{data.email || '—'}</span></div>
                        <div className="md:col-span-4"><span className="text-slate-400 block text-[10px]">Residential Address</span><span className="font-semibold text-slate-800">{data.address || '—'}</span></div>
                      </div>
                    </div>

                    {/* 2. Employment & Salary Details */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="esi-employment-details-card">
                      <div className="flex items-center justify-between mb-3 border-b border-slate-200/80 pb-2">
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                          <Briefcase className="w-4 h-4 text-[#0052FF]" /> 2. Employment Details
                        </h4>
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full border border-emerald-200">
                          ESI Eligible (Gross ≤ ₹21,000)
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                        <div><span className="text-slate-400 block text-[10px]">Company Name</span><span className="font-semibold text-slate-800">{data.company_name || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Date of Joining</span><span className="font-semibold text-slate-800">{data.date_of_joining || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Department</span><span className="font-semibold text-slate-800">{data.department_name || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Designation</span><span className="font-semibold text-slate-800">{data.designation_name || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Work Location / Branch</span><span className="font-semibold text-slate-800">{data.location_name || data.branch_name || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Gross Salary</span><span className="font-bold text-emerald-700 font-mono">₹{Number(data.gross_salary || grossSalary).toLocaleString('en-IN')}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">ESI Eligibility</span><span className="font-bold text-emerald-600">Eligible</span></div>
                      </div>
                    </div>

                    {/* 3. ESI & Dispensary Details */}
                    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm" data-testid="esi-specific-details-card">
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2 flex items-center gap-1.5">
                        <Shield className="w-4 h-4 text-[#0052FF]" /> 3. ESI Details &amp; Dispensary Preference
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-700 mb-1">Existing ESI Number / IP Number (if allocated)</label>
                          <input
                            type="text"
                            value={data.esi_number || ''}
                            disabled={readOnly}
                            onChange={e => setField('esi_number', e.target.value)}
                            placeholder="e.g. 31001234560000001"
                            className="w-full border border-slate-300 rounded px-3 py-2 text-xs"
                            data-testid="input-esi-number"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-700 mb-1">Previous ESI Number (if any)</label>
                          <input
                            type="text"
                            value={data.prev_esi_number || ''}
                            disabled={readOnly}
                            onChange={e => setField('prev_esi_number', e.target.value)}
                            placeholder="Previous IP Number"
                            className="w-full border border-slate-300 rounded px-3 py-2 text-xs"
                            data-testid="input-prev-esi-number"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-700 mb-1">Preferred ESI Dispensary / Branch Office</label>
                          <input
                            type="text"
                            value={data.dispensary_name || ''}
                            disabled={readOnly}
                            onChange={e => setField('dispensary_name', e.target.value)}
                            placeholder="e.g. ESI Dispensary T.Nagar / Ambattur"
                            className="w-full border border-slate-300 rounded px-3 py-2 text-xs"
                            data-testid="input-esi-dispensary"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-700 mb-1">ESI Doctor / Hospital Name (Optional)</label>
                          <input
                            type="text"
                            value={data.esi_doctor_name || ''}
                            disabled={readOnly}
                            onChange={e => setField('esi_doctor_name', e.target.value)}
                            placeholder="Doctor name"
                            className="w-full border border-slate-300 rounded px-3 py-2 text-xs"
                            data-testid="input-esi-doctor"
                          />
                        </div>
                      </div>
                    </div>

                    {/* 4. Family Details & Dependents */}
                    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="esi-family-details-card">
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">
                        4. FAMILY DETAILS &amp; DEPENDENTS (FOR ESI COVERAGE)
                      </h4>
                      <FamilyDetailsSection data={data} setData={setData} readOnly={readOnly} />
                    </div>

                    {/* 5. Audit Trail Card */}
                    {currentForm && (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="esi-audit-trail-card">
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">5. Form Audit Trail &amp; Workflow History</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                          <div>
                            <span className="text-slate-400 block text-[10px]">Created At</span>
                            <span className="font-mono text-slate-800">{currentForm.created_at ? currentForm.created_at.slice(0, 16).replace('T', ' ') : '—'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Last Updated</span>
                            <span className="font-mono text-slate-800">{currentForm.updated_at ? currentForm.updated_at.slice(0, 16).replace('T', ' ') : '—'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Submitted Date</span>
                            <span className="font-mono text-slate-800">{currentForm.submitted_at ? currentForm.submitted_at.slice(0, 16).replace('T', ' ') : 'Not Submitted'}</span>
                          </div>
                          {currentForm.decided_at && (
                            <>
                              <div>
                                <span className="text-slate-400 block text-[10px]">Decision Date</span>
                                <span className="font-mono text-slate-800">{currentForm.decided_at.slice(0, 16).replace('T', ' ')}</span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px]">Decision Status</span>
                                <StatusBadge status={currentForm.status} />
                              </div>
                              {currentForm.decision_remarks && (
                                <div>
                                  <span className="text-slate-400 block text-[10px]">HR Remarks</span>
                                  <span className="font-semibold text-slate-800">{currentForm.decision_remarks}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : tab === 'health_insurance' ? (
                !isHealthInsuranceEligible ? (
                  <div className="p-8 text-center space-y-4" data-testid="health-not-eligible-view">
                    <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
                      <AlertCircle className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">Health Insurance Not Applicable</h3>
                    <p className="text-xs text-slate-600 max-w-md mx-auto leading-relaxed">
                      Health Insurance is not applicable because your Gross Salary (<b>₹{Number(grossSalary).toLocaleString('en-IN')}</b>) is ₹21,000 or below.
                    </p>
                    <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-3.5 py-1.5 rounded-lg text-xs font-semibold">
                      Health Insurance Eligibility: Gross Salary &gt; ₹21,000
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6" data-testid="health-insurance-form-section">
                    {/* Header Banner */}
                    <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white rounded-xl p-5 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                      <div>
                        <span className="bg-blue-500/30 text-blue-200 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-blue-400/20">Group Mediclaim / GMC Policy</span>
                        <h3 className="text-xl font-bold mt-2">Health Insurance Declaration</h3>
                        <p className="text-xs text-blue-200 mt-0.5">Health Insurance Eligibility: Gross Salary &gt; ₹21,000</p>
                      </div>
                      <div className="text-right text-xs text-slate-300">
                        <p><span className="text-slate-400">Gross Salary:</span> <b>₹{Number(grossSalary).toLocaleString('en-IN')}</b></p>
                        <p className="mt-1"><span className="text-slate-400">Status:</span> <b className="uppercase text-amber-300">{currentForm?.status || 'Draft'}</b></p>
                      </div>
                    </div>

                    {/* 1. Auto-Populated Self Details from Employee Master */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="health-self-details-card">
                      <div className="flex items-center justify-between mb-3 border-b border-slate-200/80 pb-2">
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                          <UsersIcon className="w-4 h-4 text-[#0052FF]" /> 1. SELF DETAILS (AUTO-POPULATED FROM EMPLOYEE MASTER)
                        </h4>
                        <span className="text-[10px] bg-blue-100 text-[#0052FF] font-bold px-2 py-0.5 rounded-full border border-blue-200">
                          Read-Only Master Link
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                        <div><span className="text-slate-400 block text-[10px]">Employee Name</span><span className="font-semibold text-slate-800">{data.employee_name || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Employee Code</span><span className="font-semibold text-slate-800">{data.employee_code || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Gender</span><span className="font-semibold text-slate-800">{data.gender || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Date of Birth</span><span className="font-semibold text-slate-800">{data.date_of_birth || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Mobile Number</span><span className="font-semibold text-slate-800">{data.mobile || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Email</span><span className="font-semibold text-slate-800">{data.email || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Blood Group</span><span className="font-semibold text-slate-800">{data.blood_group || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Marital Status</span><span className="font-semibold text-slate-800">{data.marital_status || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Department</span><span className="font-semibold text-slate-800">{data.department_name || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Designation</span><span className="font-semibold text-slate-800">{data.designation_name || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Date of Joining</span><span className="font-semibold text-slate-800">{data.date_of_joining || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Work Location / Branch</span><span className="font-semibold text-slate-800">{data.location_name || data.branch_name || '—'}</span></div>
                        <div><span className="text-slate-400 block text-[10px]">Gross Salary</span><span className="font-bold text-emerald-700 font-mono">₹{Number(data.gross_salary || grossSalary).toLocaleString('en-IN')}</span></div>
                        <div className="md:col-span-3"><span className="text-slate-400 block text-[10px]">Residential Address</span><span className="font-semibold text-slate-800">{data.address || '—'}</span></div>
                      </div>
                    </div>

                    {/* 2. Family Details & Dependents Section */}
                    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="health-family-details-card">
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">
                        2. FAMILY DETAILS &amp; DEPENDENTS
                      </h4>
                      <FamilyDetailsSection data={data} setData={setData} readOnly={readOnly} />
                    </div>

                    {/* 3. Form Audit Trail & Workflow History */}
                    {currentForm && (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="health-audit-trail-card">
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">3. Form Audit Trail &amp; Workflow History</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                          <div>
                            <span className="text-slate-400 block text-[10px]">Created At</span>
                            <span className="font-mono text-slate-800">{currentForm.created_at ? currentForm.created_at.slice(0, 16).replace('T', ' ') : '—'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Last Updated</span>
                            <span className="font-mono text-slate-800">{currentForm.updated_at ? currentForm.updated_at.slice(0, 16).replace('T', ' ') : '—'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Submitted Date</span>
                            <span className="font-mono text-slate-800">{currentForm.submitted_at ? currentForm.submitted_at.slice(0, 16).replace('T', ' ') : 'Not Submitted'}</span>
                          </div>
                          {currentForm.decided_at && (
                            <>
                              <div>
                                <span className="text-slate-400 block text-[10px]">Decision Date</span>
                                <span className="font-mono text-slate-800">{currentForm.decided_at.slice(0, 16).replace('T', ' ')}</span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px]">Decision Status</span>
                                <StatusBadge status={currentForm.status} />
                              </div>
                              {currentForm.decision_remarks && (
                                <div>
                                  <span className="text-slate-400 block text-[10px]">HR Remarks</span>
                                  <span className="font-semibold text-slate-800">{currentForm.decision_remarks}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : tab === 'acknowledgement' ? (
                <div className="space-y-6" data-testid="acknowledgement-form-section">
                  {/* Header Banner */}
                  <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white rounded-xl p-5 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                    <div>
                      <span className="bg-blue-500/30 text-blue-200 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-blue-400/20">HECS Statutory Form</span>
                      <h3 className="text-xl font-bold mt-2">Hubert Enviro Care Systems</h3>
                      <p className="text-xs text-blue-200 mt-0.5">Employee HR Policy Manual &amp; Handbook Acknowledgement</p>
                    </div>
                    <div className="text-right text-xs text-slate-300">
                      <p><span className="text-slate-400">Date:</span> <b>{data.confirmation_date || new Date().toISOString().slice(0, 10)}</b></p>
                      <p className="mt-1"><span className="text-slate-400">Status:</span> <b className="uppercase text-amber-300">{currentForm?.status || 'Pending'}</b></p>
                    </div>
                  </div>

                  {/* Auto-Populated Read-Only Employee Master Fields */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">1. Employee Master Details (Auto-Populated)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                      <div><span className="text-slate-400 block text-[10px]">Employee Name</span><span className="font-semibold text-slate-800">{data.employee_name || '—'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Employee Code</span><span className="font-semibold text-slate-800">{data.employee_code || '—'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Department</span><span className="font-semibold text-slate-800">{data.department_name || '—'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Designation</span><span className="font-semibold text-slate-800">{data.designation_name || '—'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Location</span><span className="font-semibold text-slate-800">{data.location_name || '—'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Date of Joining</span><span className="font-semibold text-slate-800">{data.date_of_joining || '—'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Company</span><span className="font-semibold text-slate-800">{data.company_name || 'Hubert Enviro Care Systems'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Business Unit</span><span className="font-semibold text-slate-800">{data.business_unit || 'HECS Main Unit'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Work Email</span><span className="font-semibold text-slate-800">{data.work_email || '—'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Mobile</span><span className="font-semibold text-slate-800">{data.mobile || '—'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Reporting Manager</span><span className="font-semibold text-slate-800">{data.reporting_manager_name || '—'}</span></div>
                      <div><span className="text-slate-400 block text-[10px]">Employee Status</span><span className="font-semibold text-slate-800">{data.employment_status || 'Active'}</span></div>
                    </div>
                  </div>

                  {/* Official Acknowledgement Statement */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 text-xs leading-relaxed text-slate-700 shadow-sm">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2">2. Acknowledgement Statement</h4>
                    <p>
                      I, <b>{data.employee_name || 'Employee'}</b> acknowledge that I have received a copy of the Hubert
                      Enviro Care Systems Employee HR Policy Manual dated: 20/05/2026. I understand that this
                      employee manual replaces any and all prior verbal and written communications regarding
                      HECS working conditions, policies, procedures, appeal processes, standard of conduct
                      and benefits, etc and that it is also applicable to all subsidiaries, units of HECS.
                    </p>
                    <p>
                     I understand that the working conditions, policies, procedures, appeal processes, and
                      benefits described in this handbook are confidential and may not be distributed in any way
                      nor discussed with anyone who is not an employee of HECS LTD.
                    </p>
                    <p>
                      I have read and understand the contents of this manual and will act in accord with
                      these policies, procedures and standards of conduct as a condition of my employment with
                      HECS LTD (either permanent, probationary, apprentice, temp or contract) and failing which I
                      am aware that the company may initiate appropriate action against me.
                    </p>
                    <p>
                      I understand that if I have questions or concerns at any time about the handbook or the
                      Standards of Conduct, I will consult my immediate supervisor, my supervisor's manager,
                      the Human Resources staff, or the management for clarification.
                    </p>
                    <p>
                      I understand that HECS Ltd may modify or change the policies mentioned in the manual
                      at any time through separate notifications (circulars, emails, etc) regardless of whether the
                      manual has been revised.
                    </p>
                    <p>
                      I understand that this acknowledgement / copy of the acknowledgement will be inserted in
                      my personal file.
                    </p>
                    <p className="font-semibold text-slate-900 border-t border-slate-100 pt-3">
                      I confirm that this acknowledgement shall become part of my Employee Personal File.
                    </p>
                  </div>

                  {/* Mandatory Confirmation Checkbox */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="cb-ack-confirm"
                      checked={!!data.accepted_terms}
                      disabled={readOnly}
                      onChange={e => setField('accepted_terms', e.target.checked)}
                      data-testid="cb-ack-confirm"
                      className="mt-0.5 h-4 w-4 rounded border-amber-300 text-[#0052FF] focus:ring-[#0052FF]"
                    />
                    <label htmlFor="cb-ack-confirm" className="text-xs font-medium text-amber-900 cursor-pointer select-none">
                      I have read, understood and agree to comply with the Employee Handbook. <span className="text-red-500">*</span>
                    </label>
                  </div>

                  {/* Digital Signature Drawing & Upload */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">3. Employee Digital Signature <span className="text-red-500">*</span></h4>
                    <SignatureCanvas
                      readOnly={readOnly}
                      existingSignature={data.digital_signature}
                      onSave={sigData => setField('digital_signature', sigData)}
                    />
                  </div>
                </div>
              ) : (
                schema.map(sec => {
                  const isPersonal = sec.section === 'Personal';
                  const isEducation = tab === 'joining' && (sec.section.startsWith('Education') || sec.section === 'Education (Highest)');
                  const isPrevEmployer = tab === 'joining' && sec.section === 'Previous Employer';

                  return (
                    <div key={sec.section}>
                      {tab === 'joining' && isPersonal && (
                        <div className="mb-6 border-b border-slate-100 pb-6" data-testid="joining-photo-section">
                          <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-100 pb-2 mb-4">Employee Passport Size Photo</h3>
                          <div className="flex flex-col sm:flex-row items-center gap-6">
                            {/* Photo Preview / Placeholder */}
                            <div 
                              className="relative w-32 h-40 border-2 border-dashed border-slate-300 rounded-lg overflow-hidden flex items-center justify-center bg-slate-50 transition hover:border-[#0052FF]"
                              onDragOver={(e) => { e.preventDefault(); }}
                              onDrop={async (e) => {
                                e.preventDefault();
                                if (readOnly) return;
                                const file = e.dataTransfer.files?.[0];
                                if (file) await handlePhotoUpload(file);
                              }}
                            >
                              {(data.photoUrl || data.photo_url) ? (
                                <div data-testid="joining-photo-preview" className="w-full h-full">
                                  <AuthenticatedImage 
                                    src={data.photoUrl || data.photo_url} 
                                    alt="Passport Preview" 
                                    className="w-full h-full object-cover" 
                                    fallbackText="No Photo Uploaded"
                                  />
                                </div>
                              ) : (
                                <div className="text-center p-3 text-slate-400">
                                  <UsersIcon className="w-8 h-8 mx-auto mb-1 opacity-60" />
                                  <span className="text-[10px]">Drag & Drop<br/>or Upload</span>
                                </div>
                              )}
                            </div>

                            {/* Control Buttons */}
                            {!readOnly && (
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    id="joining-photo-upload"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (file) await handlePhotoUpload(file);
                                    }}
                                    data-testid="joining-photo-file-input"
                                  />
                                  {(data.photoUrl || data.photo_url) ? (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        data-testid="btn-replace-photo"
                                        onClick={() => document.getElementById('joining-photo-upload')?.click()}
                                      >
                                        Replace Image
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        data-testid="btn-remove-photo"
                                        className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                        onClick={handlePhotoRemove}
                                      >
                                        Remove Image
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        onClick={() => document.getElementById('joining-photo-upload')?.click()}
                                        className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
                                        size="sm"
                                        data-testid="btn-upload-photo"
                                      >
                                        Upload Photo
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center gap-1"
                                        data-testid="btn-camera-photo"
                                        onClick={() => {
                                          setShowCameraModal(true);
                                          startCamera();
                                        }}
                                      >
                                        <Camera className="w-4 h-4" /> Camera
                                      </Button>
                                    </>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-500">
                                  Supports JPG, JPEG, PNG, WEBP. Max 2 MB. Min 200 × 200 resolution.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {isEducation ? (
                        <EducationSection data={data} setData={setData} readOnly={readOnly} />
                      ) : isPrevEmployer ? (
                        <PreviousEmployerSection data={data} setData={setData} readOnly={readOnly} />
                      ) : (
                        <>
                          <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-100 pb-2 mb-3">{sec.section}</h3>
                          {sec.repeater ? (
                            <div className="space-y-3">
                              {(data[sec.repeater] || []).map((row, idx) => (
                                <div key={idx} className="border border-slate-200 rounded-md p-3 relative">
                                  {!readOnly && (
                                    <button
                                      onClick={() => removeRepeaterRow(sec.repeater, idx)}
                                      data-testid={`remove-${sec.repeater}-${idx}`}
                                      className="absolute top-2 right-2 text-slate-400 hover:text-red-500">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {sec.fields.map(f => (
                                      <div key={f.key}>
                                        <label className="text-[11px] font-medium text-slate-600">{f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}</label>
                                        <FieldInput
                                          f={f}
                                          value={row[f.key]}
                                          disabled={readOnly}
                                          onChange={v => setRepeaterRow(sec.repeater, idx, f.key, v)}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              {!readOnly && (
                                <button
                                  type="button"
                                  onClick={() => addRepeaterRow(sec.repeater)}
                                  data-testid={`add-${sec.repeater}`}
                                  className="text-xs text-[#0052FF] hover:underline flex items-center gap-1"
                                >
                                  <Plus className="w-3.5 h-3.5" /> Add {sec.section === 'Dependents' ? 'Dependent' : 'Nominee'}
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {sec.fields.map(f => (
                                <div key={f.key}>
                                  <label className="text-[11px] font-medium text-slate-600">{f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}</label>
                                  <FieldInput
                                    f={f}
                                    value={data[f.key]}
                                    disabled={readOnly}
                                    onChange={v => setField(f.key, v)}
                                    error={tab === 'joining' ? fieldErrors[f.key] : undefined}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {!loading && (
            <>
              {/* Validation error banner for Joining Form */}
              {tab === 'joining' && submitAttempted && Object.keys(fieldErrors).length > 0 && (
                <div className="mx-5 mb-0 mt-0 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="validation-error-banner">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-700 text-xs font-semibold mb-1">Please fix the following errors before submitting:</p>
                      <ul className="text-red-600 text-xs list-disc list-inside space-y-0.5">
                        {[...new Set(Object.values(fieldErrors))].map((msg, i) => <li key={i}>{msg}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/60">
                <Button
                  variant="outline"
                  onClick={() => save(false)}
                  disabled={saving || readOnly || (tab === 'esi_application' && !isEsiEligible) || (tab === 'health_insurance' && !isHealthInsuranceEligible)}
                  data-testid="btn-save-draft"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Draft
                </Button>
                <Button
                  onClick={() => save(true)}
                  disabled={saving || readOnly || (tab === 'esi_application' && !isEsiEligible) || (tab === 'health_insurance' && !isHealthInsuranceEligible) || (tab === 'joining' && submitAttempted && Object.keys(fieldErrors).length > 0)}
                  data-testid="btn-submit-form"
                  className="bg-[#0052FF] hover:bg-[#0043D1] text-white disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit for HR Review
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Camera Capture Modal */}
      {showCameraModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="camera-modal">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-900">Take Passport Photo</h3>
              <button 
                onClick={() => {
                  setShowCameraModal(false);
                  stopCamera();
                }} 
                className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-slate-950 aspect-[3/4] max-w-[240px] mx-auto flex items-center justify-center border-2 border-slate-200 shadow-inner">
                <video 
                  ref={videoRef} 
                  playsInline 
                  muted 
                  className={`w-full h-full object-cover ${cameraStream ? '' : 'opacity-0'}`} 
                />
                <canvas ref={canvasRef} className="hidden" />
                {!cameraStream && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-3 p-6 text-center">
                    <Camera className="w-10 h-10 opacity-60" />
                    {cameraError 
                      ? <p className="text-xs text-red-400">{cameraError}</p>
                      : <p className="text-xs">Initializing camera…</p>}
                    <Button 
                      onClick={startCamera} 
                      variant="outline" 
                      className="bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    >
                      Retry Camera
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-center text-xs text-slate-500">
                Align your face in the center of the camera and click Capture.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCameraModal(false);
                  stopCamera();
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={capturePhoto} 
                disabled={!cameraStream}
                className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
              >
                Capture & Upload
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
